<template>
  <div v-if="!page_loading" class="branded-fare-upsell mb-2">
    <div v-if="upsells_loading">
      <!-- la risposta alle branded tarda leggermente ad arrivare, lascio uno spinner nell'attesa per evitare uno scatto inatteso dell'interfaccia che può spiazzare -->
      <loader class="my-4" :show_timing_slot="true" />
    </div>
    <content-section
      v-else-if="flight_offers_upsell.length > 0"
      icon="flight_ticket"
      title="Tariffe"
      description="Il costo si intende per tutti i passeggeri. I servizi inclusi sono soggetti a regole e condizioni della compagnia e, in alcuni casi, l'esercizio di alcune opzioni incluse (modifiche, cancellazione, ecc.) può richiedere costi aggiuntivi."
    >
      <branded-fare-upsell-swiper
        class="branded-fare-upsell__slider"
        :flight_offers_upsell="flight_offers_upsell"
        :selected_flight_offer_id="selected_flight_offer_id"
        :swiper_params="swiper_params"
        :flight_offer_default="flight_offer_default"
        :amenities_by_offer_id="amenities_by_offer_id"
        @select-flight-offer="handle_select_flight_offer"
        @swiper-initialized="slide_to_fare"
      />
      <branded-fare-upsell-table
        class="branded-fare-upsell__table"
        :flight_offers_upsell="flight_offers_upsell"
        :selected_flight_offer_id="selected_flight_offer_id"
        :table_amenities="table_amenities"
        :offers_amenities="offers_amenities"
        :flight_offer_default="flight_offer_default"
        @select-flight-offer="handle_select_flight_offer"
      />
    </content-section>
  </div>
</template>

<script>
import {
  get_upsell,
  get_branded_fare,
  find_selected_upsell,
  filter_visible_upsells,
  choose_upsell,
} from "@liveflight/providers";
import { select_flight_offer } from "@liveflight/utilities/select-flight-offer";
import { get_config } from "@/utilities/commons";
import { max } from "mathjs";
import ContentSection from "@liveflight/components/blocks/wrappers/content-section.vue";
import BrandedFareUpsellSwiper from "./branded-fare-upsell-swiper.vue";
import BrandedFareUpsellTable from "./branded-fare-upsell-table.vue";

export default {
  name: "branded-fare-upsell",
  props: {
    flight_offer: Object,
    page_loading: Boolean,
  },
  components: {
    "content-section": ContentSection,
    "branded-fare-upsell-swiper": BrandedFareUpsellSwiper,
    "branded-fare-upsell-table": BrandedFareUpsellTable,
  },
  data() {
    return {
      upsells_loading: true,
      flight_offer_default: {},
      flight_offers_upsell: [],
      swiper_params: {
        slidesPerView: 1,
        spaceBetween: 10,
        loop: false,
        speed: 1000,
        button_offset: 30,
        breakpoints: {
          480: {
            slidesPerView: 1,
          },
          600: {
            slidesPerView: 2,
          },
          1024: {
            slidesPerView: 3,
          },
        },
      },
      //tabella
      table_amenities: [],
      offers_amenities: {},
      // Kept separate from the offer prop so the placeholder amenity
      // (for LCC fares that ship none) doesn't leak onto the store-held offer.
      amenities_by_offer_id: {},
    };
  },
  computed: {
    travelers: {
      get() {
        return this.$store.state.liveflight.travelers;
      },
      set(value) {
        this.$store.commit("liveflight/travelers", value);
      },
    },
    selected_flight_offer_id() {
      const selected = find_selected_upsell(
        this.flight_offers_upsell,
        this.flight_offer,
      );
      const { id = "" } = selected || {};
      return id;
    },
  },
  mounted() {
    this.upsells_loading = true;
    this.$store.commit("liveflight/flight_offer_has_upsell", false);

    // Guard: non chiamare upselling se flight_offer non è valido (es. localStorage corrotto con array vuoto)
    if (!this.flight_offer || !this.flight_offer.id) {
      this.upsells_loading = false;
      return;
    }

    // Capability-gated: provider resolves to null when brandedFareUpsell is off.
    get_upsell(this.flight_offer)
      .then((flight_offer_data) => {
        if (!Array.isArray(flight_offer_data) || flight_offer_data.length === 0) {
          return;
        }

        // brandedUpsell.show = 0 disables the block; positive integers
        // cap the number of variants shown (default included).
        const max_count = Math.max(0, get_config("guiSettings.brandedUpsell.show", 1));
        if (max_count <= 0) {
          this.$store.commit("liveflight/flight_offer_has_upsell",false);
          return;
        }

        const default_offer = flight_offer_data[0];
        const traveler_count = (this.travelers || []).length;

        this.flight_offer_default = default_offer;
        // Sort ascending by raw grandTotal — markup is a flat multiplier
        // so the relative order matches the post-markup displayed prices.
        this.flight_offers_upsell = filter_visible_upsells({
          upsells: flight_offer_data,
          default_offer,
          traveler_count,
          max_count,
        }).slice().sort((a, b) => {
          const a_price = Number((a.price && a.price.grandTotal) || 0);
          const b_price = Number((b.price && b.price.grandTotal) || 0);
          return a_price - b_price;
        });

        this.$store.commit(
          "liveflight/flight_offer_has_upsell",
          this.flight_offers_upsell.length > 0,
        );

        const PLACEHOLDER_AMENITY = {
          code: "XXX",
          description: "FARE",
          isChargeable: false,
          amenityType: "MISSING_AMENITIES",
        };
        this.flight_offers_upsell.forEach((f) => {
          this.offers_amenities[f.id] = [];

          // Read amenities into a separate map instead of mutating the
          // store-held offer prop (placeholder would leak across re-mounts).
          const { fareDetailsBySegment = [] } = (f.travelerPricings && f.travelerPricings[0]) || {};
          const { amenities: raw_amenities } = fareDetailsBySegment[0] || {};
          const amenities = Array.isArray(raw_amenities) && raw_amenities.length > 0
            ? raw_amenities
            : [PLACEHOLDER_AMENITY];
          this.amenities_by_offer_id = {
            ...this.amenities_by_offer_id,
            [f.id]: amenities,
          };

          amenities.forEach((amenity) => {
            if (amenity.isChargeable == false) {
              // inclusa in questa tariffa
              if (
                this.table_amenities.filter((a) => a.code === amenity.code)
                  .length == 0
              )
                this.table_amenities.push(amenity);

              this.offers_amenities[f.id].push(amenity.code);
            }
          });
        });
      })
      .finally(() => {
        this.upsells_loading = false;
      });
  },
  methods: {
    handle_select_flight_offer(value) {
      if (!value || !value.id) {
        return;
      }

      const item =
        value.id === this.selected_flight_offer_id
          ? this.flight_offer_default
          : value;

      // Provider "pick" action — Amadeus resolves synchronously, others
      // may round-trip to confirm the choice before it hits the store.
      choose_upsell(item).then((chosen) => {
        if (!chosen) {
          return;
        }
        select_flight_offer(chosen);
        this.scroll_top();
      });
    },
    slide_to_fare(swiper_instance) {
      const { swiper } = swiper_instance || {};
      if (!swiper) {
        return;
      }
      // Slide to selected flight offer index
      try {
        const selectedIndex = this.flight_offers_upsell.findIndex(
          (upsell) =>
            get_branded_fare(this.flight_offer) === get_branded_fare(upsell),
        );

        swiper.slideTo(max(0, selectedIndex));
      } catch (e) {
        this.log("branded-fare-upsell.slide_to_fare", e);
      }
    },
  },
};
</script>

<style lang="scss">
.branded-fare-upsell {
  &__top {
    display: flex;
    margin-bottom: 0;
  }
  &__heading {
    flex: 1;
  }
  &__slider {
    @include media-breakpoint-up(md) {
      display: none;
    }
  }
  &__content {
    margin-bottom: $grid-gutter-width;
    width: calc(100% + #{$grid-gutter-width/2});
    margin-left: -$grid-gutter-width/4;
    padding: 0 20px;
    position: relative;

    swiper-container {
      width: 100%;
    }

    swiper-slide {
      height: auto !important;
    }
  }
  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    padding: 6px;
    z-index: 1;
    cursor: pointer;
    svg {
      width: 14px;
      height: 14px;
    }
  }
  &__prev,
  &__next {
    @extend .branded-fare-upsell__arrow;
    &.branded-fare-upsell__prev {
      left: 0;

      svg {
        transform: rotate(-90deg);
      }
    }
    &.branded-fare-upsell__next {
      right: 0;

      svg {
        transform: rotate(90deg);
      }
    }
  }
  &__wrapper {
    height: 100%;
    .panel {
      height: 100%;
      margin-top: 0;
      margin: 0 $grid-gutter-width/4;
      max-width: calc(100% - 2px - #{$grid-gutter-width/2});
      display: flex;
      flex-direction: column;
      .content {
        padding: 6px 0;
      }
    }

    &:not(&--selected) {
      .panel {
        * {
          fill: $green;
        }
      }
    }
    &--selected {
      .panel {
        background-color: $gray-100;
        border: 2px solid var(--primary);
        margin-bottom: -1px;
        .fullwidth,
        * > div {
          border-color: $gray-200;
        }
      }
    }
  }
  &__title {
    font-size: 16px;
    text-transform: capitalize;
  }
  &__price {
    font-size: 14px;
    //@include font-primary-bold;
  }
  &__amenity {
    &__wrapper {
      display: flex;
      flex-direction: column;
    }
    &__top {
      display: flex;
      flex-wrap: nowrap;
      flex: 1;
      align-items: center;
    }
    &__icon {
      width: 28px;
      margin-right: 5px;
    }
    &__text {
      flex: 1;
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden; 
      @include font-primary-regular;
    } 
    &__name {
      display: flex;
      align-items: center;
      svg {
        --fill: var(--primary) !important;
        width: 14px;
        height: 14px;
      }
    }
  }
  &__actions {
    margin-top: $grid-gutter-width/2;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  &__separator {
    @extend .panel--separator !optional;
    width: 100%;
    margin-top: 13px;
    margin-bottom: 13px;
  }
  &__show-all {
    cursor: pointer;
    text-decoration: underline;
    font-size: $font-size-sm;
    color: $cta;
    text-align: right;
    margin-top: $grid-gutter-width;
  }
  &__modal {
    .modal-content {
      overflow: hidden;
      padding-bottom: $grid-gutter-width/2;
    }
    .modal-body {
      @include media-breakpoint-up(md) {
        display: grid;
        grid-template-columns: 50% 50%;
        .branded-fare-upsell__amenity__wrapper {
          height: 100%;
        }
      }
    }
  }

  &__table {
    display: none;
    @include media-breakpoint-up(md) {
      display: block;
    }
    &__scrollable {
      max-height: 455px;
      overflow-y: auto;
    }
    .table {
      thead {
        .sticky-top {
          background-color: #f8f9fa;
        }
      }

      &--sticky-row {
        position: sticky;
        bottom: 0;
        background: $white;

        .branded-fare-upsell__actions {
          margin-top: 10px;
        }
        td {
          position: relative;
          &:before {
            content: "";
            position: absolute;
            bottom: 100%;
            left: 0;
            width: 100%;
            height: 40px;
            background: linear-gradient(
              180deg,
              transparent 0%,
              $white 100%
            ) !important;
          }
        }
      }
      tr:nth-last-child(2) {
        td {
          padding-bottom: 40px;
        }
      }

      td {
        text-align: center;
        font-weight: $vb-font-weight-semibold;
        &:first-child {
          text-align: left;
          max-width: 350px;
        }
        &.selected {
          background: $light-blue;
          color: var(--primary);
          &:before {
            background: linear-gradient(
              180deg,
              transparent 0%,
              $light-blue 100%
            ) !important;
          }
          .table--amenity--ok svg path {
            fill: var(--primary);
          }
          .table--amenity--ko svg path {
            fill: $gray-400;
          }
        }
      }

      &--amenity {
        &--ok {
          svg path {
            fill: $green;
          }
        }
        &--ko {
          svg path {
            fill: $gray-400;
          }
        }
      }
    }
  }
}
</style>
